// frontend-react/src/services/lineupHistoryApi.js
// API service for lineup history endpoints

import apiService from './apiService';

/**
 * Fetch lineup history for a date range
 *
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise} Response with lineup snapshots
 */
export const fetchLineupHistory = async (leagueId, teamId, startDate, endDate) => {
  try {
    const response = await apiService.get(
      `/api/leagues/${leagueId}/teams/${teamId}/lineup-history`,
      {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching lineup history:', error);
    throw error;
  }
};

/**
 * Fetch available dates with lineup snapshots
 * (Uses the same endpoint as box scores)
 *
 * @param {string} leagueId - League UUID
 * @param {string} teamId - Team UUID
 * @param {number} [seasonYear] - Season year (omit to let backend read from league)
 * @returns {Promise} Response with available dates
 */
export const fetchAvailableSnapshotDates = async (leagueId, teamId, seasonYear) => {
  try {
    const params = {};
    if (seasonYear) {
      params.season_year = seasonYear;
    }
    const response = await apiService.get(
      `/api/leagues/${leagueId}/teams/${teamId}/available-dates`,
      { params }
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching available snapshot dates:', error);
    throw error;
  }
};

/**
 * Helper: Get date N days ago in YYYY-MM-DD format
 */
export const getDaysAgo = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

/**
 * Helper: Get today's date in YYYY-MM-DD format
 */
export const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

/**
 * Helper: Format date for display
 */
export const formatDisplayDate = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00'); // Noon to avoid timezone issues
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

/**
 * Helper: Format date for display (short)
 */
export const formatShortDate = (dateStr) => {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
